import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { dictionary } from "@/i18n/translations";
import { api, buildAuthHeaders } from "@/lib/api";
import { setDocumentMeta } from "@/lib/seo";

export default function DashboardAdminPage({ lang }) {
  const copy = dictionary[lang];
  const { session, user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [inviteCodes, setInviteCodes] = useState([]);
  const [allowlist, setAllowlist] = useState([]);

  const [invitePayload, setInvitePayload] = useState({
    role: "staff",
    expires_in_days: 7,
    max_uses: 5,
  });
  const [allowlistPayload, setAllowlistPayload] = useState({ email: "", role: "staff" });

  useEffect(() => {
    setDocumentMeta({
      lang,
      title: `${copy.dashboardNav.admin} | ${copy.nav.brand}`,
      description: copy.dashboardAdmin.subtitle,
    });
  }, [copy.dashboardAdmin.subtitle, copy.dashboardNav.admin, copy.nav.brand, lang]);

  const fetchAdminData = async () => {
    if (user?.role !== "admin") {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const headers = { headers: buildAuthHeaders(session) };
      const [pendingResponse, inviteResponse, allowlistResponse] = await Promise.all([
        api.get("/admin/users/pending", headers),
        api.get("/admin/invite-codes", headers),
        api.get("/admin/allowlist", headers),
      ]);

      setPendingUsers(pendingResponse.data || []);
      setInviteCodes(inviteResponse.data || []);
      setAllowlist(allowlistResponse.data || []);
    } catch {
      toast.error(copy.common.requestFailed);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
  }, [user?.role]);

  const approveUser = async (userId) => {
    try {
      await api.post(
        `/admin/users/${userId}/approve`,
        {},
        { headers: buildAuthHeaders(session) },
      );
      toast.success(copy.common.approveSuccess);
      fetchAdminData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || copy.common.requestFailed);
    }
  };

  const createInviteCode = async () => {
    try {
      await api.post("/admin/invite-codes", invitePayload, {
        headers: buildAuthHeaders(session),
      });
      toast.success(copy.common.inviteSuccess);
      fetchAdminData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || copy.common.requestFailed);
    }
  };

  const addAllowlist = async () => {
    try {
      await api.post("/admin/allowlist", allowlistPayload, {
        headers: buildAuthHeaders(session),
      });
      setAllowlistPayload((prev) => ({ ...prev, email: "" }));
      toast.success(copy.common.allowlistSuccess);
      fetchAdminData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || copy.common.requestFailed);
    }
  };

  if (user?.role !== "admin") {
    return (
      <Card className="border-gold-500/20 bg-white/90" data-testid="dashboard-admin-forbidden-card">
        <CardHeader>
          <CardTitle data-testid="dashboard-admin-forbidden-title">{copy.dashboardNav.admin}</CardTitle>
        </CardHeader>
        <CardContent data-testid="dashboard-admin-forbidden-message">
          {copy.dashboard.staffOnly}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-admin-page">
      <section className="space-y-2" data-testid="dashboard-admin-header">
        <h1 className="text-3xl font-semibold text-stone-900 sm:text-4xl" data-testid="dashboard-admin-title">
          {copy.dashboardNav.admin}
        </h1>
        <p className="text-sm text-stone-600" data-testid="dashboard-admin-subtitle">
          {copy.dashboardAdmin.subtitle}
        </p>
      </section>

      {loading && <p data-testid="dashboard-admin-loading">{copy.common.loading}</p>}

      <div className="grid gap-5 xl:grid-cols-3" data-testid="dashboard-admin-grid">
        <Card data-testid="dashboard-admin-pending-card">
          <CardHeader>
            <CardTitle data-testid="dashboard-admin-pending-title">{copy.dashboard.pendingUsers}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingUsers.length === 0 && <p data-testid="dashboard-admin-pending-empty">{copy.dashboard.noPending}</p>}
            {pendingUsers.map((item) => (
              <div key={item.id} className="rounded-xl border border-stone-200 p-3" data-testid={`dashboard-admin-pending-item-${item.id}`}>
                <p className="font-medium" data-testid={`dashboard-admin-pending-name-${item.id}`}>{item.full_name}</p>
                <p className="text-xs text-stone-600" data-testid={`dashboard-admin-pending-email-${item.id}`}>{item.email}</p>
                <Button className="mt-2" onClick={() => approveUser(item.id)} data-testid={`dashboard-admin-pending-approve-${item.id}`}>
                  {copy.dashboard.approve}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="dashboard-admin-invites-card">
          <CardHeader>
            <CardTitle data-testid="dashboard-admin-invites-title">{copy.dashboard.invitePanel}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Select value={invitePayload.role} onValueChange={(value) => setInvitePayload((prev) => ({ ...prev, role: value }))}>
              <SelectTrigger data-testid="dashboard-admin-invites-role-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="staff" data-testid="dashboard-admin-invites-role-staff">{copy.dashboard.roleStaff}</SelectItem>
                <SelectItem value="admin" data-testid="dashboard-admin-invites-role-admin">{copy.dashboard.roleAdmin}</SelectItem>
              </SelectContent>
            </Select>

            <Input type="number" min="1" max="90" value={invitePayload.expires_in_days} onChange={(event) => setInvitePayload((prev) => ({ ...prev, expires_in_days: Number(event.target.value) }))} data-testid="dashboard-admin-invites-days-input" />
            <Input type="number" min="1" max="100" value={invitePayload.max_uses} onChange={(event) => setInvitePayload((prev) => ({ ...prev, max_uses: Number(event.target.value) }))} data-testid="dashboard-admin-invites-uses-input" />

            <Button onClick={createInviteCode} data-testid="dashboard-admin-invites-create-button">{copy.dashboard.createInvite}</Button>

            <div className="space-y-2" data-testid="dashboard-admin-invites-list">
              {inviteCodes.map((invite) => (
                <div key={invite.code} className="rounded-xl border border-stone-200 p-3 text-xs" data-testid={`dashboard-admin-invites-item-${invite.code}`}>
                  <p className="font-semibold" data-testid={`dashboard-admin-invites-code-${invite.code}`}>{invite.code}</p>
                  <p data-testid={`dashboard-admin-invites-role-${invite.code}`}>{invite.role}</p>
                  <p data-testid={`dashboard-admin-invites-usage-${invite.code}`}>{invite.used_count}/{invite.max_uses}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="dashboard-admin-allowlist-card">
          <CardHeader>
            <CardTitle data-testid="dashboard-admin-allowlist-title">{copy.dashboard.allowlistList}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              type="email"
              value={allowlistPayload.email}
              onChange={(event) => setAllowlistPayload((prev) => ({ ...prev, email: event.target.value }))}
              data-testid="dashboard-admin-allowlist-email-input"
            />

            <Select value={allowlistPayload.role} onValueChange={(value) => setAllowlistPayload((prev) => ({ ...prev, role: value }))}>
              <SelectTrigger data-testid="dashboard-admin-allowlist-role-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="staff" data-testid="dashboard-admin-allowlist-role-staff">{copy.dashboard.roleStaff}</SelectItem>
                <SelectItem value="admin" data-testid="dashboard-admin-allowlist-role-admin">{copy.dashboard.roleAdmin}</SelectItem>
              </SelectContent>
            </Select>

            <Button onClick={addAllowlist} data-testid="dashboard-admin-allowlist-add-button">{copy.dashboard.addAllowlist}</Button>

            <div className="space-y-2" data-testid="dashboard-admin-allowlist-list">
              {allowlist.map((item) => (
                <div key={item.email} className="rounded-xl border border-stone-200 p-3 text-xs" data-testid={`dashboard-admin-allowlist-item-${item.email}`}>
                  <p data-testid={`dashboard-admin-allowlist-email-${item.email}`}>{item.email}</p>
                  <p data-testid={`dashboard-admin-allowlist-role-${item.email}`}>{item.role}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
